import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import BrandPattern from './BrandPattern';
import TextLines from './TextLines';

interface Props {
  /** Background image URL (also used as the video poster when `video` is set) */
  image?: string;
  /** Optional background video. `image` is used as the poster/fallback. */
  video?: { desktop: string; mobile?: string };
  /** Optional eyebrow line above the title */
  eyebrow?: string;
  title: ReactNode;
  /** Optional script accent shown above title */
  scriptAccent?: string;
  description?: ReactNode;
  children?: ReactNode;
  /** Hero height. 'full' = 100vh-ish, 'short' = ~70vh */
  height?: 'short' | 'tall' | 'full';
  /** Image overlay strength */
  overlay?: 'light' | 'dark' | 'gradient';
  /** Brand topographic pattern over the image (off for the video hero) */
  pattern?: boolean;
  align?: 'left' | 'center';
}

const heightClasses: Record<NonNullable<Props['height']>, string> = {
  short: 'min-h-[70vh]',
  tall: 'min-h-[85vh]',
  full: 'min-h-screen',
};

const overlayClasses: Record<NonNullable<Props['overlay']>, string> = {
  light: 'bg-navy-900/40',
  dark: 'bg-navy-900/70',
  gradient:
    'bg-gradient-to-b from-navy-900/80 via-navy-900/60 to-navy-900/85',
};

const Hero: React.FC<Props> = ({
  image,
  video,
  eyebrow,
  title,
  description,
  children,
  height = 'tall',
  overlay = 'gradient',
  pattern = true,
  align = 'center',
}) => {
  const alignText = align === 'center' ? 'text-center mx-auto' : 'text-left';
  return (
    <section
      className={`relative flex items-center overflow-hidden bg-navy-900 text-white ${heightClasses[height]}`}
    >
      {video ? (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={image}
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        >
          {video.mobile && (
            <source media="(max-width: 768px)" src={video.mobile} type="video/mp4" />
          )}
          <source src={video.desktop} type="video/mp4" />
        </video>
      ) : (
        image && (
          <img
            src={image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
          />
        )
      )}
      <div className={`absolute inset-0 ${overlayClasses[overlay]}`} />
      {pattern && <BrandPattern opacity={0.22} />}

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-10 py-24 md:py-32">
        <div className={`max-w-4xl ${alignText}`}>
          {eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 text-xs md:text-sm font-semibold uppercase tracking-widest text-tan-500"
            >
              {eyebrow}
            </motion.p>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] uppercase tracking-tight"
          >
            {typeof title === 'string' ? <TextLines text={title} /> : title}
          </motion.h1>
          {description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-6 max-w-2xl text-base md:text-xl leading-relaxed text-white/85"
              style={{ marginInline: align === 'center' ? 'auto' : undefined }}
            >
              {typeof description === 'string' ? <TextLines text={description} /> : description}
            </motion.p>
          )}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className={`mt-10 flex flex-wrap gap-4 ${align === 'center' ? 'justify-center' : ''}`}
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
